import React from 'react';
import { Link } from 'react-router-dom';
import { styled } from '@stitches/react';
import { useTranslation } from 'react-i18next';

// 页面容器
const PageContainer = styled('div', {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: '4rem'
});

// 英雄区块
const HeroSection = styled('section', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  padding: '4rem 1rem',
  background: 'linear-gradient(135deg, $backgroundPrimary 0%, $backgroundSecondary 100%)',
  borderRadius: '16px',
  marginBottom: '2rem'
});

// 标题样式
const Title = styled('h1', {
  fontSize: '2.5rem',
  fontWeight: 'bold',
  marginBottom: '1rem',
  '@media (max-width: 768px)': {
    fontSize: '2rem'
  }
});

// 副标题样式
const Subtitle = styled('p', {
  fontSize: '1.2rem',
  maxWidth: '800px',
  margin: '0 auto 2rem',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 主要内容区块
const ContentSection = styled('section', {
  display: 'flex',
  flexDirection: 'column',
  gap: '2rem'
});

// 内容行，可以水平或垂直显示
const ContentRow = styled('div', {
  display: 'flex',
  gap: '2rem',
  '@media (max-width: 768px)': {
    flexDirection: 'column'
  },
  variants: {
    reverse: {
      true: {
        flexDirection: 'row-reverse',
        '@media (max-width: 768px)': {
          flexDirection: 'column'
        }
      }
    }
  }
});

// 内容列
const ContentColumn = styled('div', {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  gap: '1.5rem'
});

// 内容标题
const ContentTitle = styled('h2', {
  fontSize: '2rem',
  fontWeight: 'bold',
  marginBottom: '0.5rem',
  '@media (max-width: 768px)': {
    fontSize: '1.5rem'
  }
});

// 内容描述
const ContentDescription = styled('p', {
  fontSize: '1rem',
  color: '$textSecondary',
  lineHeight: 1.6
});

// 特性列表
const FeatureList = styled('ul', {
  listStyle: 'none',
  padding: 0,
  margin: '1rem 0',
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem'
});

// 特性项
const FeatureItem = styled('li', {
  display: 'flex',
  alignItems: 'flex-start',
  gap: '1rem'
});

// 特性图标
const FeatureIcon = styled('div', {
  width: '24px',
  height: '24px',
  borderRadius: '50%',
  backgroundColor: '$primary',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0
});

// 特性文本
const FeatureText = styled('p', {
  fontSize: '1rem',
  lineHeight: 1.5,
  margin: 0
});

// 图片容器
const ImageContainer = styled('div', {
  flex: 1,
  height: '300px',
  borderRadius: '12px',
  overflow: 'hidden',
  background: '$backgroundSecondary'
});

// 图片样式
const Image = styled('img', {
  width: '100%',
  height: '100%',
  objectFit: 'cover'
});

// 操作按钮容器
const ActionButtons = styled('div', {
  display: 'flex',
  gap: '1rem',
  marginTop: '2rem',
  '@media (max-width: 768px)': {
    flexDirection: 'column'
  }
});

// 主要按钮
const PrimaryButton = styled(Link, {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.75rem 1.5rem',
  backgroundColor: '$primary',
  color: 'white',
  borderRadius: '8px',
  textDecoration: 'none',
  fontWeight: 'medium',
  transition: 'background-color 0.2s ease',
  '&:hover': {
    backgroundColor: '$primaryDark'
  }
});

// 次要按钮
const SecondaryButton = styled(Link, {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0.75rem 1.5rem',
  backgroundColor: 'transparent',
  color: '$primary',
  border: '1px solid $primary',
  borderRadius: '8px',
  textDecoration: 'none',
  fontWeight: 'medium',
  transition: 'background-color 0.2s ease, color 0.2s ease',
  '&:hover': {
    backgroundColor: '$primaryLight',
    color: '$primary'
  }
});

/**
 * 云挖矿概览页面组件
 * 介绍云挖矿服务的主要特点和优势
 */
const CloudMiningOverview: React.FC = () => {
  const { t } = useTranslation();
  
  return (
    <PageContainer>
      <HeroSection>
        <Title>{t('products.cloudMining.hero.title')}</Title>
        <Subtitle>{t('products.cloudMining.hero.subtitle')}</Subtitle>
        <ActionButtons>
          <PrimaryButton to="/products/cloud-mining/plans">
            {t('products.cloudMining.hero.viewPlans')}
          </PrimaryButton>
          <SecondaryButton to="/products/cloud-mining/calculator">
            {t('products.cloudMining.hero.calculateEarnings')}
          </SecondaryButton>
        </ActionButtons>
      </HeroSection>
      
      <ContentSection>
        {/* 介绍区块 */}
        <ContentRow>
          <ContentColumn>
            <ContentTitle>{t('products.cloudMining.intro.title')}</ContentTitle>
            <ContentDescription>{t('products.cloudMining.intro.description')}</ContentDescription>
            <FeatureList>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>{t('products.cloudMining.intro.feature1')}</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>{t('products.cloudMining.intro.feature2')}</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>{t('products.cloudMining.intro.feature3')}</FeatureText>
              </FeatureItem>
            </FeatureList>
          </ContentColumn>
          <ImageContainer>
            <Image src="/images/products/cloud-mining-intro.jpg" alt="Cloud Mining" />
          </ImageContainer>
        </ContentRow>
        
        {/* 优势区块 */}
        <ContentRow reverse>
          <ContentColumn>
            <ContentTitle>{t('products.cloudMining.advantages.title')}</ContentTitle>
            <ContentDescription>{t('products.cloudMining.advantages.description')}</ContentDescription>
            <FeatureList>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>{t('products.cloudMining.advantages.advantage1')}</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>{t('products.cloudMining.advantages.advantage2')}</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>{t('products.cloudMining.advantages.advantage3')}</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>✓</FeatureIcon>
                <FeatureText>{t('products.cloudMining.advantages.advantage4')}</FeatureText>
              </FeatureItem>
            </FeatureList>
          </ContentColumn>
          <ImageContainer>
            <Image src="/images/products/cloud-mining-advantages.jpg" alt="云挖矿优势" />
          </ImageContainer>
        </ContentRow>
        
        {/* 合约类型区块 */}
        <ContentRow>
          <ContentColumn>
            <ContentTitle>{t('products.cloudMining.contracts.title')}</ContentTitle>
            <ContentDescription>{t('products.cloudMining.contracts.description')}</ContentDescription>
            <FeatureList>
              <FeatureItem>
                <FeatureIcon>1</FeatureIcon>
                <FeatureText>{t('products.cloudMining.contracts.type1')}</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>2</FeatureIcon>
                <FeatureText>{t('products.cloudMining.contracts.type2')}</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>3</FeatureIcon>
                <FeatureText>{t('products.cloudMining.contracts.type3')}</FeatureText>
              </FeatureItem>
              <FeatureItem>
                <FeatureIcon>4</FeatureIcon>
                <FeatureText>{t('products.cloudMining.contracts.type4')}</FeatureText>
              </FeatureItem>
            </FeatureList>
            <PrimaryButton to="/products/cloud-mining/plans">
              {t('products.cloudMining.contracts.viewPlans')}
            </PrimaryButton>
          </ContentColumn>
          <ImageContainer>
            <Image src="/images/products/cloud-mining-contracts.jpg" alt="云挖矿合约" />
          </ImageContainer>
        </ContentRow>
      </ContentSection>
    </PageContainer>
  );
};

export default CloudMiningOverview; 